import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAnchor,
  faSearch,
  faStore,
  faCoins,
  faUsers,
  faInbox,
  faHeart,
  faCalendarAlt,
  faFileAlt,
  faShip,
} from '@fortawesome/free-solid-svg-icons';
import './EmptyState.css';

/**
 * Preset configurations for common empty states
 */
const EMPTY_STATE_PRESETS = {
  default: {
    icon: faAnchor,
    title: 'Nothing here yet',
    description: 'This area is empty. Check back later or take an action to add content.',
  },
  search: {
    icon: faSearch,
    title: 'No results found',
    description: "We couldn't find anything matching your search. Try different keywords or filters.",
  },
  listings: {
    icon: faStore,
    title: 'No business listings',
    description: 'There are no business listings to display. Be the first to add your business!',
  },
  tokens: {
    icon: faCoins,
    title: 'No tokens yet',
    description: "You don't have any Doubloons yet. Earn them by exploring participating businesses, or top up a Doubloon pack to unlock bonus partner perks.",
  },
  favorites: {
    icon: faHeart,
    title: 'No favorites saved',
    description: "You haven't saved any favorites yet. Browse listings and click the heart icon to save them here.",
  },
  users: {
    icon: faUsers,
    title: 'No users found',
    description: 'There are no users matching your criteria.',
  },
  inbox: {
    icon: faInbox,
    title: 'Inbox is empty',
    description: "You're all caught up! No new messages or notifications.",
  },
  events: {
    icon: faCalendarAlt,
    title: 'No upcoming events',
    description: "There are no events scheduled. Check back soon or create your own event!",
  },
  documents: {
    icon: faFileAlt,
    title: 'No documents',
    description: 'There are no documents to display.',
  },
  voyage: {
    icon: faShip,
    title: 'Set sail for adventure!',
    description: "Your journey begins here. Start exploring to discover what awaits.",
  },
};

/**
 * Empty State Component
 * Displays a friendly message when there's no content to show.
 *
 * Accepts either the legacy pair props (actionText/onAction, secondaryActionText/onSecondaryAction)
 * or a modern `actions` array: [{ label, onClick, primary? }]
 */
const EmptyState = ({
  preset,
  icon,
  title,
  description,
  // Legacy single-action props (still supported)
  actionText,
  onAction,
  secondaryActionText,
  onSecondaryAction,
  // Modern multi-action array: [{ label, onClick, primary }]
  actions,
  size = 'medium',
  className = '',
  children,
}) => {
  const presetConfig = preset ? EMPTY_STATE_PRESETS[preset] || EMPTY_STATE_PRESETS.default : {};

  const displayIcon        = icon        || presetConfig.icon        || faAnchor;
  const displayTitle       = title       || presetConfig.title       || 'Nothing here yet';
  const displayDescription = description || presetConfig.description || '';

  // Normalise actions: merge legacy props into a unified array
  const resolvedActions = actions && actions.length > 0
    ? actions
    : [
        actionText && onAction          ? { label: actionText,          onClick: onAction,          primary: true  } : null,
        secondaryActionText && onSecondaryAction ? { label: secondaryActionText, onClick: onSecondaryAction, primary: false } : null,
      ].filter(Boolean);

  return (
    <div className={`empty-state empty-state-${size} ${className}`}>
      <div className="empty-state-icon">
        <FontAwesomeIcon icon={displayIcon} />
      </div>

      <h3 className="empty-state-title">{displayTitle}</h3>

      {displayDescription && (
        <p className="empty-state-description">{displayDescription}</p>
      )}

      {children && <div className="empty-state-content">{children}</div>}

      {resolvedActions.length > 0 && (
        <div className="empty-state-actions">
          {resolvedActions.map((action, i) => (
            <button
              key={i}
              className={`empty-state-btn ${action.primary ? 'primary' : 'secondary'}`}
              onClick={action.onClick}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmptyState;
